import React, {useState, useEffect} from "react";
import {Link, useLocation, useNavigate} from "react-router-dom";
import {
  MagnifyingGlassIcon,
  ArrowLeftIcon,
  UserCircleIcon,
  Squares2X2Icon,
} from "@heroicons/react/24/outline";

import DarkModeToggle from "./DarkModeToggle";
import DebugToggle from "./DebugToggle";
import {Button} from "./ui/button";

/**
 * The masthead. A real paper announces three things before anything else: who
 * it is, what day it is, and what sections exist.
 *
 * It needs no sticky/condensed variant: the app renders this outside the
 * scrolling container (see App.js), so the plate stays on screen at all times
 * and a stand-in bar would only ghost behind it.
 */

const SECTIONS = [
  {label: "Home", to: "/"},
  {label: "Explore", to: "/explore"},
  {label: "All stories", to: "/stories"},
];

const editionDate = () =>
  new Date().toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

const Header = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const isSearch = location.pathname === "/search";

  useEffect(() => {
    if (isSearch) document.getElementById("search-input")?.focus();
  }, [isSearch]);

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  if (isSearch) {
    return (
      <header className="sticky top-0 z-30 border-b border-rule bg-paper/95 backdrop-blur">
        <div className="container flex items-center gap-3 py-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/")}
            aria-label="Back to front page"
          >
            <ArrowLeftIcon className="size-5" />
          </Button>
          <form onSubmit={handleSearchSubmit} className="relative flex-grow">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-muted" />
            <input
              id="search-input"
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search Pembury"
              autoFocus
              className="h-10 w-full rounded-md border border-[hsl(var(--control-border))] bg-[hsl(var(--control-bg))] pl-9 pr-3 font-sans text-body text-ink placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </form>
        </div>
      </header>
    );
  }

  const utilities = (
    <div className="flex items-center gap-0.5">
      <Button variant="ghost" size="icon" asChild aria-label="Explore">
        <Link to="/explore">
          <Squares2X2Icon className="size-5" />
        </Link>
      </Button>
      <Button variant="ghost" size="icon" asChild aria-label="Your profile">
        <Link to="/profile">
          <UserCircleIcon className="size-5" />
        </Link>
      </Button>
      <DebugToggle />
      <DarkModeToggle />
      <Button
        variant="ghost"
        size="icon"
        onClick={() => navigate("/search")}
        aria-label="Search"
      >
        <MagnifyingGlassIcon className="size-5" />
      </Button>
    </div>
  );

  return (
    /* The plate: dateline, wordmark, sections. */
      <header className="border-b border-rule bg-paper">
        <div className="container">
          {/* Dateline strip */}
          <div className="flex items-center justify-between border-b border-rule py-2">
            <p className="font-sans text-kicker uppercase tracking-[0.09em] text-ink-tertiary">
              <span className="hidden sm:inline">{editionDate()}</span>
              <span className="sm:hidden">Today's edition</span>
            </p>
            <p className="hidden font-sans text-kicker uppercase tracking-[0.09em] text-ink-tertiary md:block">
              Personalised edition
            </p>
          </div>

          {/* Wordmark */}
          {/* Stacked on a phone; optically centred once there is room for the
              wordmark to sit between the two utility clusters. */}
          <div className="relative flex flex-col items-center gap-3 py-4 sm:flex-row sm:justify-between sm:py-7">
            <div className="hidden sm:block sm:w-40">{null}</div>
            <Link
              to="/"
              className="text-center sm:absolute sm:left-1/2 sm:-translate-x-1/2"
              aria-label="Pembury, front page"
            >
              <span className="block font-serif text-[2rem] font-bold leading-none tracking-[-0.03em] text-ink sm:text-[3.25rem]">
                Pembury
              </span>
              <span className="mt-1.5 block font-sans text-kicker uppercase tracking-[0.34em] text-masthead">
                The Daily Record
              </span>
            </Link>
            <div className="sm:ml-auto">{utilities}</div>
          </div>
        </div>

        {/* Section rail, sitting on the masthead's own rule */}
        <nav className="border-t border-ink/85">
          <div className="container flex items-center gap-1 overflow-x-auto py-1.5">
            {SECTIONS.map((section) => {
              const active = location.pathname === section.to;
              return (
                <Link
                  key={section.to}
                  to={section.to}
                  className={`whitespace-nowrap px-3 py-1.5 font-sans text-meta font-semibold uppercase tracking-[0.08em] transition-colors duration-150 ease-out ${
                    active
                      ? "text-masthead"
                      : "text-ink-secondary hover:text-ink"
                  }`}
                >
                  {section.label}
                </Link>
              );
            })}
          </div>
        </nav>
      </header>
  );
};

export default Header;
